import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { statecityData } from "../../../../src/shared/enums";
import axios from "axios";
import ImageOverlayUploader from "./installation/subTable/ImageUploader";
import deleteImageFromS3 from "../../../helperFunctions/deleteImageFromS3";
import uploadImageToS3 from "../../../helperFunctions/uploadImageToS3";
import Decrypt from "../../../shared/utils/Decrypt";
import GlobalButton from "../../../shared/utils/GlobalButton";

const ModalToOpenEditJobs = ({
  open,
  handleClose,
  selectedJob,
  setSelectedJob,
  handleSaveJob,
  onIPAssignmentSuccess,
  fetchJobsData,
  showJobPayField = true,
  installationCheck,
  service,
}) => {
  const [errors, setErrors] = useState({});
  const imageRef = useRef();
  const [originalJob, setOriginalJob] = useState(null);
  const [deletedImage, setDeletedImage] = useState(null);
  const [remark, setRemark] = useState("");

  const [cityArray, setCityArray] = useState([]);
  const [openInstallationPartnerList, setOpenInstallationPartnerList] =
    useState(false);
  const [installationPartnerList, setInstallationPartnerList] = useState([]);
  const [selectedInstallationPartner, setSelectedInstallationPartner] =
    useState("");
  const [ipLoading, setIpLoading] = useState(false);
  const [isSearchIp, setIsSearchIp] = useState("");

  const [debouncedSearchIp, setDebouncedSearchIp] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchIp(isSearchIp);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [isSearchIp]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSaving, setIsSaving] = useState(false); // Add loading state for save

  const cityApiKey = process.env.REACT_APP_FETCH_STATE_CITY_API_KEY;
  const serverURL = process.env.REACT_APP_URL;
  useEffect(() => {
    if (open && selectedJob) {
      setOriginalJob(JSON.parse(JSON.stringify(selectedJob)));
      if (selectedJob?.installationRemark) {
        setRemark(selectedJob.installationRemark);
      } else {
        setRemark("");
      }
    }
  }, [open]);

  const getUpdatedFields = (oldData, newData) => {
    const updated = {};

    const IGNORE_FIELDS = [
      "_id",
      "createdAt",
      "updatedAt",
      "__v",
      "installationPartnerId",
    ];

    Object.keys(newData).forEach((key) => {
      if (IGNORE_FIELDS.includes(key)) return;

      const oldVal = oldData?.[key];
      const newVal = newData?.[key];

      const isEqual = JSON.stringify(oldVal) === JSON.stringify(newVal);

      if (!isEqual) {
        updated[key] = newVal;
      }
    });

    return updated;
  };

  // Fetch cities when state changes
  useEffect(() => {
    const stateCode = statecityData.find(
      (ele) => ele.name === selectedJob?.state,
    )?.iso2;

    if (selectedJob?.state) {
      const fetchCities = async (stateCode) => {
        const headers = new Headers();
        headers.append("X-CSCAPI-KEY", cityApiKey);

        const requestOptions = {
          method: "GET",
          headers: headers,
          redirect: "follow",
        };

        try {
          const response = await fetch(
            `https://api.countrystatecity.in/v1/countries/IN/states/${stateCode}/cities`,
            requestOptions,
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const cityData = await response.json();
          setCityArray(cityData);
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      };

      fetchCities(stateCode);
    }
  }, [selectedJob?.state]);

  // Fetch installation partners
  const fetchAllInstallationPartners = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${serverURL}/installationPartner/getAllInstallationPartnersToAssign?nameAndPhoneNumber=${debouncedSearchIp}`, // Add parameter to include all details
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200) {
        setInstallationPartnerList(response.data.data || []);
      } else {
        throw new Error("Failed to fetch installation partners");
      }
    } catch (error) {
      console.error("Fetch partners error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load installation partners",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (isSearchIp) {
      fetchAllInstallationPartners();
    }
  }, [debouncedSearchIp]);

  const handleIPChangeModalOpen = async () => {
    try {
      setIpLoading(true);
      await fetchAllInstallationPartners();
      setOpenInstallationPartnerList(true);
    } catch (error) {
      console.error("Error opening IP modal:", error);
    } finally {
      setIpLoading(false);
    }
  };

  const handleIPChangeModalClose = () => {
    setOpenInstallationPartnerList(false);
    setIsSearchIp("");
    setSelectedInstallationPartner("");
  };
  const saveNewIP = async (installationPartnerId) => {
    setIpLoading(true);
    try {
      if (!selectedJob?._id) {
        throw new Error("No job selected");
      }

      const token = localStorage.getItem("token");
      const endpoint = `${serverURL}/jobs/updateJob/${selectedJob._id}`;

      const response = await axios.put(
        endpoint,
        { installationPartnerId: installationPartnerId || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Failed to update IP");
      }

      // If we assigned an IP, fetch the complete IP details
      let completeIP = null;
      if (installationPartnerId) {
        try {
          const ipResponse = await axios.get(
            `${serverURL}/installationPartner/view/${installationPartnerId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (ipResponse?.data?.success) {
            completeIP = ipResponse.data.data;
          } else {
            // Fallback: use what we have in the list
            completeIP = installationPartnerList.find(
              (ip) => ip._id === installationPartnerId,
            );
          }
        } catch (ipError) {
          console.error("Failed to fetch IP details:", ipError);
          // Fallback: use what we have in the list
          completeIP = installationPartnerList.find(
            (ip) => ip._id === installationPartnerId,
          );
        }
      }

      // Update the local job state with the new IP
      setSelectedJob((prev) => ({
        ...prev,
        installationPartnerId: completeIP,
      }));

      setSnackbar({
        open: true,
        message: installationPartnerId
          ? "Installation partner assigned successfully"
          : "IP unassigned successfully",
        severity: "success",
      });

      // Call the callback to refresh parent data
      if (typeof onIPAssignmentSuccess === "function") {
        onIPAssignmentSuccess();
      }

      // Alternatively, if you have a fetch function, call it
      if (typeof fetchJobsData === "function") {
        await fetchJobsData();
      }

      handleIPChangeModalClose();
    } catch (error) {
      console.error("Update IP error details:", error);
      console.error("Error response:", error.response?.data);

      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update installation partner",
        severity: "error",
      });
    } finally {
      setIpLoading(false);
    }
  };
  const validateFields = () => {
    const errors = {};

    if (!/^\d{10}$/.test(selectedJob?.storeOwnerPhoneNumber || ""))
      errors.storeOwnerPhoneNumber = "Invalid phone";

    if (!/^\d{6}$/.test(selectedJob?.zipCode || ""))
      errors.zipCode = "Invalid pincode";

    if (!selectedJob?.city) errors.city = "City required";
    if (!selectedJob?.state) errors.state = "State required";
    if (!selectedJob?.storeName) errors.storeName = "Store name required";
    if (!selectedJob?.address) errors.address = "Address required";
    if (!selectedJob?.storeOwnerName)
      errors.storeOwnerName = "Store Owner Name required";

    setErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!validateFields()) return;

    setIsSaving(true);

    try {
      let imageUrl = null;
      let originalImageUrl = null;
      const oldImage =
        deletedImage || originalJob?.imagesOfInstallation?.frontPhoto;
      const token = localStorage.getItem("token");

      if (installationCheck && imageRef.current) {
        const geotaggedBlob = await imageRef.current.generateImage();

        if (geotaggedBlob) {
          imageUrl = await uploadImageToS3({
            blob: geotaggedBlob,
            token,
          });
        }

        // Original, unmodified image for leftPhoto history
        const originalFile = imageRef.current.getOriginalImage();

        if (originalFile) {
          originalImageUrl = await uploadImageToS3({
            blob: originalFile,
            token,
          });
        }
      }

      const updatedFields = getUpdatedFields(originalJob, selectedJob);
      let payload = {
        _id: selectedJob._id,
        ...updatedFields,
      };

      const finalImage =
        imageUrl !== null
          ? imageUrl
          : selectedJob?.imagesOfInstallation?.frontPhoto || null;

      if (installationCheck) {
        payload.imagesOfInstallation = {
          frontPhoto: finalImage ?? null,
          leftPhoto: originalImageUrl ?? null,
          rightPhoto: selectedJob?.imagesOfInstallation?.rightPhoto ?? null,
        };

        const remarkValue = selectedJob?.installationRemark || "";
        if (remarkValue && remarkValue.trim() !== "") {
          payload.installationRemark = remarkValue;
        }

        if (finalImage) {
          payload.status = 2;
          payload.jobCompleted = true;
        } else {
          payload.status = 1; // or your pending status
          payload.jobCompleted = false;
        }
      }

      await handleSaveJob(payload);

      // delete old image only after successful save
      if (oldImage && oldImage !== finalImage) {
        try {
          await deleteImageFromS3({
            imageUrl: oldImage,
            token,
          });
        } catch (error) {
          console.error("Failed to delete old image from S3", error);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setSelectedJob((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" && { city: "" }), // Clear city when state changes
    }));

    // Clear city error when user selects a city
    if (name === "city") {
      setErrors((prev) => ({ ...prev, city: "Enter Valid City" }));
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteImage = () => {
    setDeletedImage(selectedJob?.imagesOfInstallation?.frontPhoto);

    setSelectedJob((prev) => {
      return {
        ...prev,
        imagesOfInstallation: {
          ...prev.imagesOfInstallation,
          frontPhoto: null,
        },
      };
    });
  };

  const handleModalClose = () => {
    setRemark("");
    setDeletedImage(null);
    handleClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleModalClose}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={11} sm={9} md={7} lg={6} xl={5}>
            <Box
              sx={{
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <Typography variant="h6" mb={2}>
                Edit Job - {selectedJob?.storeName}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Store Name"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter Store Name"
                    value={selectedJob?.storeName || ""}
                    onChange={(e) =>
                      setSelectedJob({
                        ...selectedJob,
                        storeName: e.target.value,
                      })
                    }
                    error={errors.storeName}
                    helperText={errors.storeName ? errors.storeName : ""}
                  />
                </Grid>
                <Grid item xs={12} md={showJobPayField ? 4 : 6}>
                  <TextField
                    fullWidth
                    label="Store Manager Name"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter Store Manager Name"
                    value={selectedJob?.storeOwnerName || ""}
                    onChange={(e) =>
                      setSelectedJob({
                        ...selectedJob,
                        storeOwnerName: e.target.value,
                      })
                    }
                    error={errors.storeOwnerName}
                    helperText={
                      errors.storeOwnerName ? "Enter Store Owner Name" : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} md={showJobPayField ? 4 : 6}>
                  <TextField
                    fullWidth
                    label="Store Manager Number"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter Store Manager Number"
                    value={selectedJob?.storeOwnerPhoneNumber || ""}
                    onChange={(e) =>
                      setSelectedJob({
                        ...selectedJob,
                        storeOwnerPhoneNumber: e.target.value,
                      })
                    }
                    onWheel={(e) => {
                      e.target.blur();
                      e.preventDefault();
                    }}
                    error={errors.storeOwnerPhoneNumber}
                    helperText={
                      errors.storeOwnerPhoneNumber
                        ? "Phone number must be 10 digits"
                        : ""
                    }
                    inputProps={{
                      maxLength: 10,
                      type: "number",
                      onWheel: (e) => e.preventDefault(),
                    }}
                  />
                </Grid>
                {showJobPayField && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Job Pay"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Enter Job Pay"
                      value={selectedJob?.paymentAmount || ""}
                      onChange={(e) =>
                        setSelectedJob({
                          ...selectedJob,
                          paymentAmount: e.target.value,
                        })
                      }
                      error={errors.paymentAmount}
                      helperText={
                        errors.paymentAmount ? "Enter Payment Amount" : ""
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter Address"
                    value={selectedJob?.address || ""}
                    onChange={(e) =>
                      setSelectedJob({
                        ...selectedJob,
                        address: e.target.value,
                      })
                    }
                    error={errors.address}
                    helperText={errors.address ? "Enter Address" : ""}
                  />
                </Grid>
                {/* State Dropdown */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>State *</InputLabel>
                    <Select
                      name="state"
                      value={selectedJob?.state || ""}
                      onChange={handleInputChange}
                      label="State *"
                    >
                      {statecityData.map((state) => (
                        <MenuItem key={state.name} value={state.name}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* City Dropdown */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={errors.city}>
                    <InputLabel>City *</InputLabel>
                    <Select
                      name="city"
                      value={selectedJob?.city || ""}
                      onChange={handleInputChange}
                      label="City *"
                      disabled={!selectedJob?.state}
                    >
                      {cityArray.length > 0 ? (
                        cityArray.map((city) => (
                          <MenuItem key={city.name} value={city.name}>
                            {city.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Select state first</MenuItem>
                      )}
                    </Select>
                    {errors.city && (
                      <Typography variant="caption" color="error">
                        Please select a city
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pin Code"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter Pin Code"
                    value={selectedJob?.zipCode || ""}
                    onChange={(e) =>
                      setSelectedJob({
                        ...selectedJob,
                        zipCode: e.target.value,
                      })
                    }
                    error={errors.zipCode}
                    helperText={errors.zipCode ? "Enter valid Pin Code" : ""}
                    inputProps={{
                      maxLength: 6,
                      type: "number",
                      onWheel: (e) => {
                        e.preventDefault();
                        e.target.blur();
                      },
                    }}
                  />
                </Grid>

                {installationCheck && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "16px",
                        p: 2,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "space-between",
                          marginBottom: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          Installation Images
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {/* Update */}
                          <Tooltip
                            title={
                              selectedJob?.imagesOfInstallation?.frontPhoto
                                ? "Update Image"
                                : "Add Image"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                imageRef.current?.triggerUpdateImage();
                              }}
                              sx={{
                                color: "#2563eb",
                                background: "#eff6ff",
                                "&:hover": {
                                  background: "#dbeafe",
                                },
                              }}
                            >
                              {selectedJob?.imagesOfInstallation?.frontPhoto ? (
                                <EditOutlinedIcon fontSize="small" />
                              ) : (
                                <AddPhotoAlternateIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>

                          {/* Delete */}
                          <Tooltip title="Delete Image">
                            <IconButton
                              size="small"
                              disabled={
                                !selectedJob?.imagesOfInstallation?.frontPhoto
                              }
                              onClick={() => {
                                handleDeleteImage();
                              }}
                              sx={{
                                color: "#dc2626",
                                background: "#fef2f2",
                                "&:hover": {
                                  background: "#fee2e2",
                                },
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        {/* Recce Image */}
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              border: "1px solid #d1d5db",
                              borderRadius: "12px",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                              height: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: "1px solid #e5e7eb",
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                Recce Image
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                p: 1,
                                display: "flex",
                                minHeight: 200,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {selectedJob?.imageOfRecceSpace ? (
                                <img
                                  src={selectedJob?.imageOfRecceSpace}
                                  alt="Recce"
                                  style={{
                                    width: "100%",
                                    maxHeight: "300px",
                                    objectFit: "contain",
                                    borderRadius: "10px",
                                  }}
                                />
                              ) : (
                                <Typography color="text.secondary">
                                  Recce as service is not selected
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>

                        {/* Last Installation Image */}
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              border: "1px solid #d1d5db",
                              borderRadius: "12px",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                              height: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: "1px solid #e5e7eb",
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                Last Installation Image
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                p: 1,
                                minHeight: 200,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {selectedJob?.imagesOfInstallation?.frontPhoto ? (
                                <img
                                  src={
                                    selectedJob?.imagesOfInstallation
                                      ?.frontPhoto
                                  }
                                  alt="Installation"
                                  style={{
                                    width: "100%",
                                    objectFit: "contain",
                                    borderRadius: "10px",
                                  }}
                                />
                              ) : (
                                <Typography color="text.secondary">
                                  No installation image available
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>

                        {/* Upload Section */}
                        <Grid item xs={12}>
                          <ImageOverlayUploader
                            ref={imageRef}
                            selectedJob={selectedJob}
                            open={open}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}

                {/* Installation Remark Field */}
                {installationCheck && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        p: 2,
                        backgroundColor: "#fff",
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          Installation Remark{" "}
                          <span
                            style={{
                              color: "#6b7280",
                              fontSize: "0.75rem",
                              fontWeight: 400,
                            }}
                          >
                            (Optional)
                          </span>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              (selectedJob?.installationRemark || "").length >
                              450
                                ? "#dc2626"
                                : "#6b7280",
                          }}
                        >
                          {(selectedJob?.installationRemark || "").length}/500
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Enter any remarks about the installation (optional)"
                        value={selectedJob?.installationRemark || remark || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRemark(value);
                          setSelectedJob({
                            ...selectedJob,
                            installationRemark: value,
                          });
                          if (errors.installationRemark) {
                            setErrors((prev) => ({
                              ...prev,
                              installationRemark: "",
                            }));
                          }
                        }}
                        error={!!errors.installationRemark}
                        helperText={
                          errors.installationRemark ||
                          "Add any installation notes here (optional)"
                        }
                        inputProps={{
                          maxLength: 500,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: errors.installationRemark
                                ? "#dc2626"
                                : "#d1d5db",
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* IP Assignment Section */}
              {service !== "Supply" &&
                selectedJob?.batchOfJobsForInstallationPartnerId && (
                  <>
                    <div className="bg-gray-100 p-4 rounded-lg mt-6 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <img
                          src={
                            selectedJob?.installationPartnerId?.profilePhoto ||
                            "/assests/AdminDashboard/userForIP.webp"
                          }
                          alt="IP"
                          className="w-12 h-12 rounded-full"
                        />
                        <div>
                          <p className="text-lg font-semibold">
                            {selectedJob?.installationPartnerId?.name ||
                              "No IP Assigned"}
                          </p>
                          <div className="flex items-center text-yellow-500">
                            <span className="ml-1 text-gray-700"></span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <GlobalButton
                            variant="warning"
                            size="small"
                            disabled={!selectedJob?.installationPartnerId}
                            isLoading={ipLoading}
                            onClick={() => saveNewIP(null)}
                          >
                            {`Unassign IP`}
                          </GlobalButton>
                          <GlobalButton
                            variant="secondary"
                            size="small"
                            onClick={handleIPChangeModalOpen}
                          >
                            {`Change IP`}
                          </GlobalButton>
                        </div>
                      </div>
                    </div>
                  </>
                )}

              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <GlobalButton
                  variant="secondary"
                  size="medium"
                  onClick={handleModalClose}
                  style={{ width: "100px" }}
                >
                  {`Cancel`}
                </GlobalButton>
                <GlobalButton
                  variant="primary"
                  size="medium"
                  isLoading={isSaving}
                  onClick={handleSave}
                  style={{ width: "100px" }}
                >
                  {`Save`}
                </GlobalButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Modal>

      {/* IP Selection Dialog */}
      <Dialog
        open={openInstallationPartnerList}
        onClose={handleIPChangeModalClose}
      >
        <DialogTitle>Select Installation Partner</DialogTitle>
        <DialogContent sx={{ height: 350, width: 450, overflow: "auto" }}>
          <FormControl fullWidth>
            <input
              type="search"
              placeholder="Search Installation Partner"
              value={isSearchIp}
              onChange={(e) => setIsSearchIp(e.target.value)}
              className="p-2 text-[14px] border border-spacing-3 mb-2"
            />
            <div className="dropdown-options border-spacing-2 gap-2">
              {installationPartnerList.map((partner) => (
                <div
                  className="border border-spacing-1 p-2 text-[14px] cursor-pointer hover:bg-gray-100"
                  key={partner._id}
                  onClick={() => {
                    setSelectedInstallationPartner(partner._id);
                    setIsSearchIp(partner.name);
                  }}
                >
                  <div className="flex flex-col gap-1">
                    <span>{partner.name}</span>
                    <span>{partner.phoneNumber}</span>
                  </div>
                </div>
              ))}
            </div>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: 2 }}>
          <GlobalButton
            variant="secondary"
            size="small"
            onClick={handleIPChangeModalClose}
            style={{ width: "100px" }}
          >
            {`Cancel`}
          </GlobalButton>
          <GlobalButton
            variant="primary"
            size="small"
            disabled={!selectedInstallationPartner}
            isLoading={ipLoading}
            onClick={() => saveNewIP(selectedInstallationPartner)}
            style={{ width: "120px" }}
          >
            {`Assign IP`}
          </GlobalButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "right", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ModalToOpenEditJobs;
